import { REGS } from '../registers';
import { SocketTypesSelect, ValveTypesSelect, VFTypesSelect } from '../constants/options';

/**
 * Which physical slot each appliance param id refers to, so a chart series can be
 * labelled with the appliance's type and user-given name instead of "Appliance 13".
 *
 * The authority for this mapping is the register *titles* in registersInit, not
 * params.js's own labels: APPLIANCE1_NAME_START is titled "Skt1 Name",
 * APPLIANCE9_NAME_START "Vlv1 Name", APPLIANCE13_NAME_START "VF 1 Name". params.js
 * disagrees with itself on the same appliances — id 13 is "Heater" there and
 * "VF Contact" in AppliancesView — which is the reason this feature exists.
 *
 * Covers ids 1-14 and stops there, by decision rather than omission (owner,
 * 2026-08-07): params ids 15-17 — Acid Dosing, Chlorine Dosing, Other Temperature —
 * keep their existing labels, and the APPLIANCE18/19 name registers are reserved and
 * unused. So there is nothing further to map here; adding 15-17 would be a product
 * change, not a gap being filled.
 *
 * Careful with the two id spaces, which are independent and happen to overlap:
 * params.js ids 18-25 are the set-points and Spa Mode, *not* appliances, while the
 * register space has its own APPLIANCE18/19. Assuming they line up would label a
 * set-point with an appliance's name.
 */
export const APPLIANCE_SLOTS = {
  1: { abbrev: 'S1', nameReg: REGS.APPLIANCE1_NAME_START, typeReg: REGS.SOCKET_1_TYPE, typeOptions: SocketTypesSelect },
  2: { abbrev: 'S2', nameReg: REGS.APPLIANCE2_NAME_START, typeReg: REGS.SOCKET_2_TYPE, typeOptions: SocketTypesSelect },
  3: { abbrev: 'S3', nameReg: REGS.APPLIANCE3_NAME_START, typeReg: REGS.SOCKET_3_TYPE, typeOptions: SocketTypesSelect },
  4: { abbrev: 'S4', nameReg: REGS.APPLIANCE4_NAME_START, typeReg: REGS.SOCKET_4_TYPE, typeOptions: SocketTypesSelect },
  5: { abbrev: 'S5', nameReg: REGS.APPLIANCE5_NAME_START, typeReg: REGS.SOCKET_5_TYPE, typeOptions: SocketTypesSelect },
  6: { abbrev: 'S6', nameReg: REGS.APPLIANCE6_NAME_START, typeReg: REGS.SOCKET_6_TYPE, typeOptions: SocketTypesSelect },
  7: { abbrev: 'S7', nameReg: REGS.APPLIANCE7_NAME_START, typeReg: REGS.SOCKET_7_TYPE, typeOptions: SocketTypesSelect },
  8: { abbrev: 'S8', nameReg: REGS.APPLIANCE8_NAME_START, typeReg: REGS.SOCKET_8_TYPE, typeOptions: SocketTypesSelect },
  9: { abbrev: 'V1', nameReg: REGS.APPLIANCE9_NAME_START, typeReg: REGS.VALVE_1_TYPE, typeOptions: ValveTypesSelect },
  10: { abbrev: 'V2', nameReg: REGS.APPLIANCE10_NAME_START, typeReg: REGS.VALVE_2_TYPE, typeOptions: ValveTypesSelect },
  11: { abbrev: 'V3', nameReg: REGS.APPLIANCE11_NAME_START, typeReg: REGS.VALVE_3_TYPE, typeOptions: ValveTypesSelect },
  12: { abbrev: 'V4', nameReg: REGS.APPLIANCE12_NAME_START, typeReg: REGS.VALVE_4_TYPE, typeOptions: ValveTypesSelect },
  13: {
    abbrev: 'VF1',
    nameReg: REGS.APPLIANCE13_NAME_START,
    typeReg: REGS.VFCONTACT_1_TYPE,
    typeOptions: VFTypesSelect,
  },
  14: {
    abbrev: 'VF2',
    nameReg: REGS.APPLIANCE14_NAME_START,
    typeReg: REGS.VFCONTACT_2_TYPE,
    typeOptions: VFTypesSelect,
  },
};

/** Shown when the controller reports the slot's type as None. */
export const NOT_SET_LABEL = 'Not Set';

/**
 * The user-given name of an appliance, or '' when it has none.
 *
 * A never-named appliance does not always decode to an empty string:
 * DisplaySocketValveVFStatusItem's isNameSet() also rejects a run of sixteen NUL
 * characters, so that is a state the hardware really produces. Stripping NULs
 * rather than comparing against one exact string covers partially-filled runs too.
 * (That component keeps its own copy of this rule; it has no test file, so it was
 * left alone rather than refactored blind — see the handoff.)
 *
 * @param {{display?: string}} [nameRegister]
 * @returns {string}
 */
export const getApplianceName = (nameRegister) => {
  if (nameRegister === undefined || typeof nameRegister.display !== 'string') return '';
  // Strip NULs only, then trim — never a blanket space-strip, since an interior
  // space is part of the name ("Spa Heater"). The directive must sit directly
  // above the code, not above these comments, or it applies to nothing.
  // eslint-disable-next-line no-control-regex
  return nameRegister.display.replace(/\u0000/g, '').trim();
};

/**
 * The register value meaning "nothing is wired to this slot". Zero in all three type
 * maps — socket_types.NONE, vfTypesEnum.NONE, and ValveTypesSelect's own 0.
 *
 * Keyed off the value rather than the option's label text on purpose: the labels are
 * user-facing copy and do get edited (`Pool/SPA` became `Pool/Spa`, `InFloor` became
 * `In Floor`), so matching the string `'None'` would make "is this slot unused?"
 * quietly dependent on wording. applianceLabel.test.js pins the assumption that each
 * map's 0 entry really is the none case, so a firmware change there fails a test
 * instead of silently mislabelling every slot.
 */
const NONE_TYPE_VALUE = 0;

const isNoneValue = (value) => value === NONE_TYPE_VALUE;

/**
 * A display label for an appliance chart series, e.g.
 *
 *   S3: Chlorinator     named
 *   S3: Sanitiser       unnamed, type set
 *   S3: Not Set         unnamed, type reported as None
 *   V2: Pool/Spa        valve — params id 10, so the slot offset matters
 *   VF1: Spa Heater     VF contact — params id 13
 *
 * Kept short deliberately: the CheckPicker renders these for *selected* entries too,
 * and a longer form ("Appliance 3 - Chlorinator (S3)") overflowed that row once a few
 * were picked. The slot abbreviation leads because it identifies the appliance more
 * usefully than the param's ordinal did — "V2" says valve 2, where "Appliance 10"
 * needs the offset table to decode.
 *
 * Returns null when no better label can be justified, so the caller keeps the
 * param's own text. That happens when:
 *
 *  - the param is not one of the appliances in APPLIANCE_SLOTS;
 *  - the controller has not responded. This one matters: registersInit seeds every
 *    register with value 0, and 0 is None for all three type maps, so an offline
 *    device would otherwise be labelled "Not Set" across the board — stating
 *    something false rather than merely missing;
 *  - the type register is absent, or holds a value the type map does not list,
 *    which means firmware knows a type this app does not.
 *
 * @param {{id?: number, text?: string}} param
 * @param {(address: number) => ({value?: number, display?: string} | undefined)} getRegisterData
 * @param {boolean} controllerResponded
 * @returns {string | null}
 */
export const getApplianceLabel = (param, getRegisterData, controllerResponded) => {
  const resolved = resolveAppliance(param, getRegisterData, controllerResponded);
  if (resolved === null) return null;
  const { slot, name, option } = resolved;
  const detail = name !== '' ? name : isNoneValue(option.value) ? NOT_SET_LABEL : option.label;
  return `${slot.abbrev}: ${detail}`;
};

/**
 * Everything both public helpers need, or null when nothing can be justified — so the
 * label and the "is it configured" decision are read off one resolution rather than
 * the second one string-matching the first's output.
 *
 * @returns {{slot: object, name: string, option: {value: number, label: string}} | null}
 */
const resolveAppliance = (param, getRegisterData, controllerResponded) => {
  const slot = APPLIANCE_SLOTS[param?.id];
  if (slot === undefined || !controllerResponded || typeof getRegisterData !== 'function') return null;

  const name = getApplianceName(getRegisterData(slot.nameReg));

  const typeRegister = getRegisterData(slot.typeReg);
  if (typeRegister === undefined) return name !== '' ? { slot, name, option: { value: -1, label: name } } : null;

  const option = slot.typeOptions.find((o) => o.value === typeRegister.value);
  if (option === undefined) return name !== '' ? { slot, name, option: { value: -1, label: name } } : null;

  return { slot, name, option };
};

/**
 * True only when the controller positively reports this appliance's slot as unused:
 * type None *and* no name. Callers disable those in the picker — selecting one plots
 * a series for hardware that is not connected to anything.
 *
 * Deliberately false whenever the answer is unknown rather than negative — no
 * response yet, no type register, an unrecognised type value — for the same reason
 * getApplianceLabel returns null there: an unknown is not a "no". Also false when the
 * appliance carries a name, since a named slot was configured by someone even if its
 * type currently reads None; that case shows the name and stays selectable.
 *
 * @param {{id?: number, text?: string}} param
 * @param {(address: number) => ({value?: number, display?: string} | undefined)} getRegisterData
 * @param {boolean} controllerResponded
 * @returns {boolean}
 */
export const isApplianceUnconfigured = (param, getRegisterData, controllerResponded) => {
  const resolved = resolveAppliance(param, getRegisterData, controllerResponded);
  return resolved !== null && resolved.name === '' && isNoneValue(resolved.option.value);
};
